import { z } from 'zod';
import {
  colorSchema,
  dimensionsSchema,
  finite,
  idSchema,
  nonNegative,
  polygonSchema,
  positive,
  transformSchema,
  vec2Schema,
} from './primitives.js';
import { STEEL_PROFILE_IDS } from '../steelProfiles.js';

const nameSchema = z.string().trim().min(1).max(120);
const noteSchema = z.string().max(4000);

export const siteSchema = z.object({
  id: idSchema,
  name: nameSchema,
  boundary: polygonSchema,
  /** Protects the position against accidental moving and turning; shape stays editable. */
  locked: z.boolean(),
  /** Second, separate protection: position and shape fixed, untouched by "unlock everything". */
  shellLocked: z.boolean().optional(),
});

export const roofTypeSchema = z.enum(['gable', 'flat', 'mono']);

export const steelProfileSchema = z.enum(STEEL_PROFILE_IDS);

/**
 * Steel frame of a hall (columns and rafters in a regular grid), derived from its size.
 * Missing fields mean the usual values; a hall without this object has the usual frame.
 */
export const hallStructureSchema = z.object({
  /** False for a hall without a worked-out structure. */
  enabled: z.boolean().optional(),
  /**
   * Steel frames (missing), concrete columns fixed in sockets with steel binders, or precast
   * concrete columns and binders.
   */
  system: z.enum(['steel', 'hybrid', 'precast']).optional(),
  /** Square cross section of the concrete columns set by hand; missing means from the height. */
  concreteColumn: z.number().min(0.3).max(1.2).optional(),
  /** Largest distance between two frames; the hall length is divided evenly. Default 5 m. */
  bay: z.number().min(2).max(12).optional(),
  /** Rows of interior columns along the hall; missing means automatic from the span. */
  interiorRows: z.number().int().min(0).max(3).optional(),
  /**
   * Interior columns under every n-th frame; the frames between rest on a beam along the row.
   * Default 2 (about every 10 m).
   */
  interiorSpacing: z.number().int().min(1).max(3).optional(),
  /** Beam along the interior column rows set by hand; missing means sized from its span. */
  beamProfile: steelProfileSchema.optional(),
  /** Profiles chosen by hand; missing means taken from the span. */
  columnProfile: steelProfileSchema.optional(),
  rafterProfile: steelProfileSchema.optional(),
  /** Columns in the gable walls between the corner columns. Default on. */
  gableColumns: z.boolean().optional(),
  /** Purlins along the hall carrying the roof panels. Default on. */
  purlins: z.boolean().optional(),
  /** Horizontal wall girts; missing means where the facade needs them (vertical panels). */
  girts: z.boolean().optional(),
  /** Crossed bracing in roof and long walls of the end bays, with eave struts. Default on. */
  bracing: z.boolean().optional(),
  /** Haunches strengthening the frame corners. Default on. */
  haunches: z.boolean().optional(),
});

export const MAX_OFFICE_FLOORS = 4;

/**
 * Floor index: 0 = ground floor (EG), 1 = first upper floor, … The last index of an indoor
 * office with a usable top slab is its roof platform.
 */
export const levelSchema = z.number().int().min(0).max(MAX_OFFICE_FLOORS);

export const buildingKindSchema = z.enum(['hall', 'office']);
export const facadeKindSchema = z.enum([
  'iso-vertical',
  'iso-horizontal',
  'trapezoid',
  'smooth',
  'brick',
]);
export const officePlacementSchema = z.enum(['indoor', 'outdoor']);
/** Hall floor in the 3D view: power-floated concrete with joints, coated, or one plain colour. */
export const hallFloorSchema = z.enum(['concrete', 'coated', 'plain']);

/**
 * Load-bearing structure of an office: slabs on its walls (missing), or a reinforced concrete
 * skeleton of columns in a grid with flat slabs or with beams between the columns.
 */
export const officeStructureSchema = z.object({
  system: z.enum(['walls', 'flat', 'beams']),
  /** Largest distance between two column axes; the outline is divided evenly. Default 7.5 m. */
  grid: z.number().min(3).max(12).optional(),
  /** Square column cross section set by hand; missing means sized from the loads. */
  columnSize: z.number().min(0.2).max(1.2).optional(),
});

/**
 * Multi-storey office/social block. Floor k starts at the sum of the clear heights and slab
 * thicknesses below it; the building's `eaveHeight` is kept equal to the total height.
 */
export const officeSchema = z.object({
  /** Built into a hall (under the hall roof, concrete top slab) or free-standing (flat roof). */
  placement: officePlacementSchema,
  floors: z
    // `color`: facade of this storey's walls when it differs from the building colour.
    .array(z.object({ clearHeight: positive, color: colorSchema.optional() }))
    .min(1)
    .max(MAX_OFFICE_FLOORS),
  /** Concrete slab including floor finish. */
  slabThickness: positive,
  /** Indoor only: the top slab is usable (e.g. storage platform) and gets a railing. */
  usableTop: z.boolean(),
  /** Edges of the usable top (outline index) where the railing was switched off by hand. */
  deckRailingOff: z.array(z.number().int().min(0)).default([]),
  /**
   * Exterior wall pieces removed on purpose, per floor (edge of that floor's outline, level),
   * e.g. for a loggia; an upper floor gets a guard railing on the slab edge there unless
   * switched off.
   */
  openFloors: z
    .array(
      z.object({
        wall: z.number().int().min(0),
        level: z.number().int().min(0),
        railing: z.boolean(),
      }),
    )
    .default([]),
  /**
   * Upper floors smaller than the floor below (Staffelgeschoss): from `level` up the floors
   * have this outline. The free area of the floor below becomes a flat roof with parapet, or a
   * walkable roof terrace with railing.
   */
  setbacks: z
    .array(
      z.object({
        level: z.number().int().min(1),
        outline: polygonSchema,
        terrace: z.boolean(),
      }),
    )
    .default([]),
  /** Own names of the areas walls drawn in a storey divide it into, kept at a point inside. */
  areaNames: z
    .array(z.object({ level: z.number().int().min(0), at: vec2Schema, name: nameSchema }))
    .max(400)
    .optional(),
  /** Load-bearing structure; missing means slabs resting on the walls. */
  structure: officeStructureSchema.optional(),
});

/**
 * Parametric rectangular block: a hall or an office building. Its exterior walls are stored
 * as regular Wall entities with `ownerId` = building id so openings can reference them.
 */
export const buildingSchema = z.object({
  id: idSchema,
  name: nameSchema,
  kind: buildingKindSchema,
  /** Center of the footprint in the ground plane. */
  center: vec2Schema,
  rotation: finite,
  width: positive,
  depth: positive,
  eaveHeight: positive,
  roofType: roofTypeSchema,
  roofPitchDeg: finite.refine((v) => v >= 0 && v < 60, { message: 'Dachneigung 0–60°' }),
  wallThickness: positive,
  color: colorSchema,
  /** Cladding of the outer walls in the 3D view. */
  facade: facadeKindSchema,
  /** Corner profiles, roof edges and the frames of gates, doors and windows. */
  trimColor: colorSchema,
  /** Leaves of sectional and rolling gates. */
  gateColor: colorSchema,
  /** Roof covering (e.g. white for a tent hall); missing means the usual grey. */
  roofColor: colorSchema.nullable().optional(),
  /** Floor of a hall; missing means concrete. */
  floorFinish: hallFloorSchema.optional(),
  /** Floors, slabs and placement; only for `kind: 'office'`. */
  office: officeSchema.nullable(),
  /**
   * Hall or office drawn point by point (outer face, counter-clockwise); null = the rectangle
   * given by center, width, depth and rotation. With an outline those describe its bounding
   * box. Only flat and mono-pitch roofs.
   */
  footprint: polygonSchema.nullable(),
  /** Mono-pitch roof over a drawn outline: the outline edge at the low eave (index). */
  eaveEdge: z.number().int().min(0).nullable(),
  /** Exterior walls removed on purpose (side numbers), e.g. where the hall facade takes over. */
  omittedWalls: z.array(z.number().int().min(0)).default([]),
  /** Steel frame of a rectangular hall; missing means the usual frame. */
  structure: hallStructureSchema.optional(),
  /** Photovoltaics on the roof: the share of the roof area it covers. */
  pv: z.object({ share: z.number().min(0.05).max(1) }).optional(),
  /** Protects position and rotation; dimensions stay editable. */
  locked: z.boolean(),
  /**
   * Second, separate protection of the shell: position, rotation, size, outline and exterior
   * walls fixed; untouched by "unlock everything", released only on purpose.
   */
  shellLocked: z.boolean().optional(),
});

export const stairTypeSchema = z.enum(['straight', 'u', 'l']);

/**
 * Stair from `fromLevel` to the floor above in an office building. Steps are derived from
 * the floor height, so the stair adapts when heights change. Local frame: x across the
 * stair, +y the walking direction of the first flight, origin at the footprint center.
 */
export const stairSchema = z.object({
  id: idSchema,
  name: nameSchema,
  buildingId: idSchema,
  fromLevel: levelSchema,
  type: stairTypeSchema,
  center: vec2Schema,
  rotation: finite,
  /** Clear width of a flight. */
  width: positive,
  /** Direction of the turn for U and L stairs. */
  turn: z.enum(['left', 'right']),
  /** Enclosed stairwell (walls with a door), typically for stairs outside the building. */
  enclosed: z.boolean(),
  /**
   * Sides of an enclosed stairwell that get a wall (front = at the first step, with the door;
   * back = behind the landing). Missing means all four.
   */
  wallSides: z
    .object({ front: z.boolean(), back: z.boolean(), left: z.boolean(), right: z.boolean() })
    .optional(),
  /** Stairwell wall pieces removed by hand (side, `-top` for the storey arrived on). */
  removedWalls: z.array(z.string()).optional(),
  /** Depth of the landing in front of the first step in an enclosed stairwell (default 1.2 m). */
  entry: nonNegative.optional(),
  /** Own going (tread depth) instead of the step rule, e.g. to fit a shorter stairwell. */
  going: positive.optional(),
  /** Risers more or fewer than the step rule gives (steeper or flatter steps). */
  riserDelta: z.number().int().min(-4).max(4).optional(),
  /** Straight stair: landing halfway up, the second flight going on in the same direction. */
  midLanding: z.boolean().optional(),
  /** Height it climbs, set by hand where the floors it is taken from do not fit. */
  rise: positive.optional(),
  /**
   * Stair in a hall up to its mezzanine: height it starts at (e.g. the 1st floor of an office
   * below the mezzanine); missing means the hall floor.
   */
  bottom: nonNegative.optional(),
  /**
   * Hall stair: the mezzanine it leads up to, set where its top end meets a mezzanine edge.
   * Missing means the mezzanine nearest to the stair.
   */
  mezzanineId: idSchema.optional(),
  /** Hall stair up to a floor of an office standing in the hall instead of a mezzanine. */
  toOffice: z.object({ officeId: idSchema, level: levelSchema }).optional(),
  /** Building whose flat roof the stair leads out onto (roof terrace), above its floors. */
  toRoof: idSchema.optional(),
  /** Depth of the exit landing at the top in walking direction (0 = arrives on the slab). */
  exitLanding: nonNegative,
  /**
   * Platform leading off the top landing to the left or right of the walking direction, as
   * wide as the stair, e.g. across the column line of a hall wall to a door.
   */
  sideExit: z
    .object({ side: z.enum(['left', 'right']), length: z.number().min(0.2).max(20) })
    .optional(),
  /** False: moved freely, without grid or docking onto walls. Missing means on. */
  snapping: z.boolean().optional(),
  /**
   * Solid body down to the floor, an open steel stair (stringers, grating steps, landings on
   * posts) or a concrete slab stair (steps on an inclined waist, landing slabs), both with free
   * space underneath. Missing means solid.
   */
  construction: z.enum(['solid', 'steel', 'slab']).optional(),
  /** Stairs of one stairwell over several floors share this id and are edited together. */
  groupId: idSchema.optional(),
  locked: z.boolean(),
});

/** Sliding handover gate in a railing: on an edge, from `offset` along it, `width` wide. */
export const railingGateSchema = z.object({
  edge: z.number().int().min(0),
  offset: nonNegative,
  width: z.number().min(0.5).max(10),
});

/** Walkable partial upper floor (Bühne) inside a hall: its top is the floor of level 1 there. */
export const mezzanineSchema = z.object({
  id: idSchema,
  name: nameSchema,
  buildingId: idSchema,
  polygon: polygonSchema,
  /** Height of the walking surface above the hall floor. */
  elevation: positive,
  slabThickness: positive,
  /** Guard rail along the edges that do not run along a hall wall. */
  railing: z.boolean(),
  /** Edges (outline index) where the railing was switched off by hand. */
  railingOff: z.array(z.number().int().min(0)).default([]),
  /** Sliding handover gates in the railing, e.g. for a forklift putting pallets onto it. */
  railingGates: z.array(railingGateSchema).optional(),
  /** Own names of the areas its walls divide it into, each kept at a point inside its area. */
  areaNames: z
    .array(z.object({ at: vec2Schema, name: nameSchema }))
    .max(200)
    .optional(),
  /** Supports under the free corners and along long free edges. */
  columns: z.boolean(),
  color: colorSchema,
  locked: z.boolean(),
});

/**
 * Column (Stütze) in a hall or an office storey, set by hand or taken over from the automatic
 * supports of a mezzanine (`mezzanineId`). Its height follows from where it stands.
 */
export const columnSchema = z.object({
  id: idSchema,
  buildingId: idSchema,
  /** Mezzanine whose supports it replaced, so they can be brought back. */
  mezzanineId: idSchema.optional(),
  level: levelSchema,
  center: vec2Schema,
  material: z.enum(['steel', 'concrete']),
  /** Cross section along its own x and y axis. */
  width: positive,
  depth: positive,
  rotation: finite,
  /** Own height above the floor it stands on; missing means up to the slab, mezzanine or eaves. */
  height: positive.optional(),
  locked: z.boolean(),
});

export const wallKindSchema = z.enum(['exterior', 'interior']);
/** Surface of interior and free walls; the shell of a hall follows the hall's facade. */
/** Surface of a freely drawn wall in 3D: plain (plastered), panels or the masonry seen. */
export const wallCladdingSchema = z.enum([
  'plain',
  'sandwich',
  'sandwich-horizontal',
  'sand-lime',
  'concrete',
  'brick',
]);

export const wallSchema = z.object({
  id: idSchema,
  start: vec2Schema,
  end: vec2Schema,
  thickness: positive,
  height: positive,
  /** Relative to the floor of `level`. */
  baseElevation: nonNegative,
  level: levelSchema,
  kind: wallKindSchema,
  cladding: wallCladdingSchema,
  /** Building or room that generated this wall; null for freely drawn walls. */
  ownerId: idSchema.nullable(),
  color: colorSchema,
  /** Owned walls follow the lock of their hall or room. */
  locked: z.boolean(),
  /**
   * Drywall linings (Vorsatzschale) on one face, from `from` to `to` along the wall, from the
   * floor of `level` in front of it up to the ceiling.
   */
  linings: z
    .array(
      z.object({
        side: z.enum(['left', 'right']),
        from: nonNegative,
        to: positive,
        level: levelSchema,
        thickness: z.number().min(0.03).max(0.5),
        color: colorSchema,
      }),
    )
    .max(20)
    .optional(),
});

export const openingTypeSchema = z.enum([
  'sectional-door',
  'rolling-door',
  /** High-speed roll-up door: PVC curtain with a vision strip, coiling into a hood above. */
  'rapid-door',
  /** Fire-rated sliding gate of galvanized steel sheet, parking beside the opening. */
  'fire-gate',
  'door',
  'double-door',
  /** Doors with an aluminium frame leaf and frosted glass panes. */
  'glass-door',
  'glass-double-door',
  /** Door-sized hole without leaf or frame (Wanddurchbruch); the wall above stays. */
  'passage',
  'double-passage',
  'window',
  'window-band',
  'loading-dock',
  /** Open part of a wall without frame (arcade, open front, joined halls); walkable. */
  'void',
]);

/**
 * Truck dock with a depressed approach: the gate sits at hall floor level, the truck stands
 * in a pit so its bed matches the floor, and the dock leveler is built into the hall floor.
 */
export const dockSchema = z.object({
  /** Depth of the depressed approach below ground (truck bed height). */
  pitDepth: positive,
  bayWidth: positive,
  /** Level standing area in front of the gate. */
  bayLength: positive,
  /** Sloped ramp from the level area back up to ground; 0 = none. */
  rampLength: nonNegative,
  levelerWidth: positive,
  levelerLength: positive,
  /** False for the second gate of a double dock: it shares the approach of its neighbour. */
  approach: z.boolean().default(true),
  /** Shift of the approach center along the wall (wall direction), e.g. to sit between two gates. */
  approachOffset: finite.default(0),
  /** Fall protection on the left / right side of the trough, seen from the yard onto the gates. */
  railingLeft: z.boolean().default(true),
  railingRight: z.boolean().default(true),
});

export const openingSchema = z.object({
  id: idSchema,
  wallId: idSchema,
  type: openingTypeSchema,
  offsetFromWallStart: nonNegative,
  width: positive,
  height: positive,
  sillHeight: nonNegative,
  /** Only for loading docks; missing values fall back to `DEFAULT_DOCK`. */
  dock: dockSchema.optional(),
  /**
   * Only for doors: hinge at the wall's start or end, leaf opening to the left or right side
   * of the wall (seen from its start). Missing: hinge at the start, opening to the left.
   */
  swing: z.object({ hinge: z.enum(['start', 'end']), side: z.enum(['left', 'right']) }).optional(),
  /**
   * Only for fire-rated sliding gates: the side of the opening the leaf slides to, towards the
   * wall's start (left seen from outside a hall) or its end. Missing means the start.
   */
  slideTo: z.enum(['start', 'end']).optional(),
  /**
   * Only for fire-rated sliding gates and high-speed doors: mounted on the other face of the
   * wall. Missing means the right face seen from the wall's start for fire-rated gates, and
   * the left face (inside a hall) for high-speed doors.
   */
  otherFace: z.boolean().optional(),
  /** Only for open wall parts: supports at both edges and at most 6 m apart. */
  posts: z.boolean().optional(),
  /** Own frame colour; missing means the trim colour of the building. */
  frameColor: colorSchema.optional(),
  /** Own colour of door and gate leaves; missing means the building's (gates) or light grey. */
  leafColor: colorSchema.optional(),
  locked: z.boolean(),
});

/**
 * Canopy (Vordach) hanging on a wall, e.g. over dock gates or an entrance: it follows the
 * wall and projects to one side of it.
 */
export const canopySchema = z.object({
  id: idSchema,
  wallId: idSchema,
  offsetFromWallStart: nonNegative,
  width: positive,
  /** Projection from the wall face. */
  depth: positive,
  /** Height of the underside at the wall above the wall's base. */
  height: positive,
  /** Fall towards the front edge. */
  pitchDeg: z.number().min(0).max(30),
  thickness: positive,
  /** Side of the wall it projects to (seen from the wall's start). */
  side: z.enum(['left', 'right']),
  /** Posts under the front edge instead of a cantilever. */
  posts: z.boolean(),
  color: colorSchema,
  locked: z.boolean(),
});

/**
 * Picture or logo on a wall face, e.g. a company lettering on a facade (a PNG keeps its
 * transparency) or a picture in a room. Positions are taken along the wall from its start.
 */
export const wallImageSchema = z.object({
  id: idSchema,
  wallId: idSchema,
  /** Uploaded image (PNG or JPEG). */
  assetId: idSchema,
  name: nameSchema,
  /** Pixel size of the image, for its aspect ratio. */
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
  /** Face it hangs on, seen from the wall's start. */
  side: z.enum(['left', 'right']),
  /** Start of the picture along the wall. */
  offsetFromWallStart: finite,
  /** Lower edge above the wall's base. */
  bottom: nonNegative,
  width: positive,
  height: positive,
  locked: z.boolean(),
});

/** Guard rail drawn point by point, on the ground (e.g. along dock ramps) or on an upper level. */
export const railingSchema = z.object({
  id: idSchema,
  points: z.array(vec2Schema).min(2).max(200),
  height: positive,
  /** Where it stands: on a level (ground, mezzanine, office floor), on a flat roof, or at a set height. */
  mount: z.enum(['level', 'roof', 'custom']),
  level: levelSchema,
  /** Building whose flat roof carries it (mount `roof`). */
  buildingId: idSchema.nullable(),
  /** Base height above ground (mount `custom`). */
  elevation: nonNegative,
  color: colorSchema,
  /** Sliding handover gates in the railing (segment = between point `edge` and the next). */
  gates: z.array(railingGateSchema).optional(),
  locked: z.boolean(),
});

export const zoneCategorySchema = z.enum([
  'production',
  'storage',
  'goods-in',
  'goods-out',
  'assembly',
  'packaging',
  'office',
  'social',
  'technical',
  'workshop',
  'open-space',
  'truck-access',
  'forklift-path',
  'walkway',
  'escape-route',
  'work-area',
  'paving',
  'green',
  'gravel',
  'crushed-stone',
  'asphalt',
  'concrete',
  'other',
]);

export const zoneSchema = z.object({
  id: idSchema,
  name: nameSchema,
  category: zoneCategorySchema,
  color: colorSchema,
  polygon: polygonSchema,
  height: positive.nullable(),
  note: noteSchema,
  buildingId: idSchema.nullable(),
  level: levelSchema,
  locked: z.boolean(),
});

export const roomTypeSchema = z.enum([
  'office',
  'meeting',
  'kitchen',
  'wc-women',
  'wc-men',
  'wc-accessible',
  'changing',
  'shower',
  'corridor',
  'technical',
  'storage',
  'other',
]);

export const roomSchema = z.object({
  id: idSchema,
  name: nameSchema,
  roomType: roomTypeSchema,
  polygon: polygonSchema,
  height: positive,
  color: colorSchema,
  note: noteSchema,
  buildingId: idSchema.nullable(),
  level: levelSchema,
  locked: z.boolean(),
});

export const clearanceSchema = z.object({
  front: nonNegative,
  back: nonNegative,
  left: nonNegative,
  right: nonNegative,
});

export const objectSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('library'), libraryId: z.string().min(1).max(120) }),
  z.object({
    kind: z.literal('asset'),
    assetId: idSchema,
    /**
     * Native bounding size of the GLB (meters, model axes). Stored in the document so scaling
     * stays reproducible even if asset metadata is temporarily unavailable.
     */
    nativeSize: dimensionsSchema,
  }),
]);

/** One move of a swept path: straight, curve, straight; forwards or backing up. */
const sweepSegmentSchema = z.object({
  angle: z.number().min(0).max(360),
  side: z.enum(['left', 'right']),
  radius: z.number().min(1).max(100),
  lead: z.number().min(0).max(200),
  tail: z.number().min(0).max(200),
  reverse: z.boolean().optional(),
});

export const placedObjectSchema = z.object({
  id: idSchema,
  name: nameSchema,
  source: objectSourceSchema,
  transform: transformSchema,
  /** Real-world target size; renderers scale the model's native bounds to match. */
  dimensions: dimensionsSchema,
  clearance: clearanceSchema.nullable(),
  color: colorSchema.nullable(),
  locked: z.boolean(),
  /** Hidden objects are left out of plan, 3D view, collisions and (optionally) the report. */
  hidden: z.boolean(),
  /** `transform.position.z` is relative to the floor of this level. */
  level: levelSchema,
  /** False: moved freely, without grid, snapping onto edges or docking. Missing means on. */
  snapping: z.boolean().optional(),
  /**
   * Pallet and shelving racks: clear height of each compartment from the bottom; compartments
   * beyond the list keep the standard (pallet racks) or share the rest evenly (shelving).
   * Missing means all standard.
   */
  rackClearHeights: z.array(z.number().min(0.1).max(10)).max(40).optional(),
  /** Pallet racks: bays (from the start of the rack) left free at the bottom to walk through. */
  rackPassages: z.array(z.number().int().min(0)).max(100).optional(),
  /** Clear height kept free in those bays; missing means 2.20 m. */
  rackPassageHeight: z.number().min(1.8).max(8).optional(),
  /**
   * Swept path (Schleppkurve) of a design vehicle: a flat planning aid that blocks nothing.
   * The object's box encloses it; the path starts at `start` in the object's own frame.
   */
  sweep: sweepSegmentSchema
    .extend({
      vehicle: z.enum(['car', 'truck', 'semi']),
      /** Further moves of a manoeuvre, each from where the vehicle stopped. */
      then: z.array(sweepSegmentSchema).max(7).optional(),
      start: vec2Schema,
    })
    .optional(),
});

export const measurementSchema = z.object({
  id: idSchema,
  start: vec2Schema,
  end: vec2Schema,
  label: z.string().max(120).nullable(),
  level: levelSchema,
  locked: z.boolean(),
});

export const roofElementTypeSchema = z.enum(['skylight-dome', 'skylight-band', 'smoke-vent']);

/**
 * Skylight, light band or smoke vent on a hall roof. Stored in the hall's local frame
 * (u along its width axis, v along its depth axis, from the hall center), so it moves and
 * rotates with the hall without any extra bookkeeping.
 */
export const roofElementSchema = z.object({
  id: idSchema,
  buildingId: idSchema,
  type: roofElementTypeSchema,
  u: finite,
  v: finite,
  width: positive,
  depth: positive,
  /** Turn relative to the hall's axes (radians); on pitched roofs always 0 (turns swap the sizes). */
  rotation: finite.default(0),
  locked: z.boolean(),
});

export const roadKindSchema = z.enum(['road', 'truck-access', 'fire-lane', 'footpath']);

/** Road or path as centerline with constant width. */
export const roadSchema = z.object({
  id: idSchema,
  name: nameSchema,
  kind: roadKindSchema,
  points: z.array(vec2Schema).min(2).max(200),
  width: positive,
  locked: z.boolean(),
});

export const parkingKindSchema = z.enum(['car', 'truck']);

/**
 * Parking area generated from stall parameters: `rows` rows of `stallsPerRow` stalls, with a
 * driving aisle in front (single row) or between the rows (double row).
 */
export const parkingSchema = z.object({
  id: idSchema,
  name: nameSchema,
  kind: parkingKindSchema,
  center: vec2Schema,
  rotation: finite,
  rows: z.union([z.literal(1), z.literal(2)]),
  stallsPerRow: z.number().int().min(1).max(200),
  stallWidth: positive,
  stallDepth: positive,
  aisleWidth: nonNegative,
  locked: z.boolean(),
});

/** `mesh` = welded double-wire panels on posts, `mobile` = temporary construction fence on feet. */
export const fenceKindSchema = z.enum(['mesh', 'mobile']);

export const fenceSchema = z.object({
  id: idSchema,
  name: nameSchema,
  points: z.array(vec2Schema).min(2).max(200),
  height: positive,
  kind: fenceKindSchema,
  color: colorSchema,
  locked: z.boolean(),
});

export const gateKindSchema = z.enum(['sliding', 'door']);

/**
 * Opening in a fence, placed on its own: `center` is the middle of the opening, local x
 * (turned by `rotation`) runs across it. Standing on a fence line, it cuts the fence there.
 * An electric cantilever sliding gate slides to the left (−x) or right (+x) when opening; a
 * single-leaf pedestrian door is hinged on that side and swings to local +y.
 */
export const gateSchema = z.object({
  id: idSchema,
  name: nameSchema,
  kind: gateKindSchema,
  center: vec2Schema,
  rotation: finite,
  width: positive,
  height: positive,
  opensTo: z.enum(['left', 'right']),
  color: colorSchema,
  locked: z.boolean(),
});

/**
 * Site plan or aerial photo under the planning, placed in model meters: image center, turn
 * (counter-clockwise) and scale in meters per image pixel, set by calibrating a known distance.
 */
export const backgroundSchema = z.object({
  assetId: idSchema,
  name: nameSchema,
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
  center: vec2Schema,
  rotation: finite,
  metersPerPixel: positive,
  opacity: z.number().min(0.05).max(1),
  visible: z.boolean(),
  locked: z.boolean(),
  /**
   * Taken from a PDF drawing: the original file and page, drawn sharp in the plan at every
   * zoom; `assetId` is the page rendered as a transparent image (3D view, fallback).
   */
  pdf: z.object({ assetId: idSchema, page: z.number().int().min(1).max(10_000) }).optional(),
  /** Drawn over the planning (to trace a plan with transparent paper), not under it. */
  onTop: z.boolean().optional(),
});

/** Plan of an upper floor (1 = 1st floor …), shown while the plan shows that floor. */
export const levelBackgroundSchema = backgroundSchema.extend({
  level: z.number().int().min(1).max(50),
});

export const annotationSchema = z.object({
  id: idSchema,
  position: vec2Schema,
  text: z.string().min(1).max(1000),
  level: levelSchema,
  locked: z.boolean(),
});
